import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Spinner from "./Spinner";
import AddTransactionForm, { type FormState } from "./AddTransactionForm";

export interface TransactionRow {
  id: string;
  "Date of Deposit": string;
  "Action": string | null;
  "Deposit Type": string | null;
  "Partner": string | null;
  "Email": string | null;
  "Valuation Status": string | null;
  "Cash/Account Transfer Amount": number | null;
  "% benefits": number | null;
  "Valuation Type": string | null;
  "Portfolio Value at the Date of Valuation": number | null;
  "Final Valuation per Txn": number | null;
  "SSN Fund Value (Per Txn)": number | null;
  "SSN Units Bought (Per Txn)": number | null;
  "SSN Total Units (Running Total)": number | null;
  "Month of Deposit": string | null;
}

type Mode = { kind: "list" } | { kind: "add" } | { kind: "edit"; row: TransactionRow };

function money(n: number | null) {
  if (n === null || n === undefined) return "-";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function num(n: number | null, digits = 2) {
  if (n === null || n === undefined) return "-";
  return n.toLocaleString("en-US", { maximumFractionDigits: digits });
}

function oneOf<T extends string>(value: string | null, options: readonly T[], fallback: T): T {
  return (options as readonly string[]).includes(value ?? "") ? (value as T) : fallback;
}

function toForm(r: TransactionRow): FormState {
  const fundValue = r["SSN Fund Value (Per Txn)"];
  return {
    date_of_deposit: r["Date of Deposit"],
    action: oneOf(r["Action"], ["Deposit", "Initial"] as const, "Deposit"),
    deposit_type: oneOf(r["Deposit Type"], ["Cash", "Account transfer"] as const, "Cash"),
    partner_email: r["Email"] ?? "",
    valuation_status: oneOf(r["Valuation Status"], ["Locked", "Pending"] as const, "Locked"),
    amount: String(r["Cash/Account Transfer Amount"] ?? ""),
    benefit_pct: String(+(((r["% benefits"] ?? 0) * 100).toFixed(4))),
    valuation_type: oneOf(r["Valuation Type"], ["", "Type 1", "Type 2"] as const, ""),
    portfolio_value: r["Portfolio Value at the Date of Valuation"] === null ? "" : String(r["Portfolio Value at the Date of Valuation"]),
    // The stored portfolio value isn't shown in the form but is kept on save.
    fund_value_override: fundValue ? String(fundValue) : "",
  };
}

export default function AdminTransactions() {
  const [rows, setRows] = useState<TransactionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "list" });
  const [deleting, setDeleting] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get<TransactionRow[]>("/transactions");
      setRows(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "Failed to load transactions");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (r: TransactionRow) => {
    const ok = window.confirm(
      `Delete this transaction?\n\n${r["Partner"]} · ${r["Date of Deposit"]} · ${money(r["Cash/Account Transfer Amount"])}\n\n` +
      "This can't be undone. Total units and the monthly summary will be updated.",
    );
    if (!ok) return;
    setDeleting(r.id);
    setNotice(null);
    try {
      await api.delete(`/transactions/${r.id}`);
      setNotice(`Deleted ${r["Partner"]}'s ${money(r["Cash/Account Transfer Amount"])} deposit on ${r["Date of Deposit"]}.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const onSaved = async (message: string) => {
    setNotice(message);
    setMode({ kind: "list" });
    await load();
  };

  const q = filter.trim().toLowerCase();
  const visible = (rows ?? []).filter((r) =>
    !q || `${r["Partner"]} ${r["Email"]} ${r["Date of Deposit"]}`.toLowerCase().includes(q),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {mode.kind === "add" && (
        <AddTransactionForm onSaved={onSaved} onCancel={() => setMode({ kind: "list" })} />
      )}
      {mode.kind === "edit" && (
        <AddTransactionForm
          key={mode.row.id}
          editId={mode.row.id}
          initial={toForm(mode.row)}
          onSaved={onSaved}
          onCancel={() => setMode({ kind: "list" })}
        />
      )}

      <div style={S.card}>
        <style>{`
          .atx-btn:hover { background: rgba(255,255,255,0.08) !important; }
          .atx-del:hover { background: rgba(224,90,90,0.15) !important; }
        `}</style>
        <div style={S.header}>
          <h3 style={S.title}>All Transactions {rows && <span style={S.count}>({rows.length})</span>}</h3>
          <div style={S.headerActions}>
            <input
              style={S.search}
              placeholder="Filter by partner, email or date"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            {mode.kind === "list" && (
              <button style={S.primaryBtn} onClick={() => { setNotice(null); setMode({ kind: "add" }); }}>
                Add transaction
              </button>
            )}
          </div>
        </div>

        {notice && <div style={S.success}>{notice}</div>}
        {error && <div style={S.error}>{error}</div>}

        {rows === null && !error ? <Spinner /> : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Date", "Partner", "Action", "Type", "Status", "Amount", "Unit Price", "Units", "Total Units", ""].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={r.id} style={{
                    ...(i % 2 ? { background: "rgba(255,255,255,0.02)" } : {}),
                    ...(mode.kind === "edit" && mode.row.id === r.id ? { background: "rgba(29,158,117,0.10)" } : {}),
                  }}>
                    <td style={{ ...S.td, color: "var(--text-secondary)" }}>{r["Date of Deposit"]}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 500 }}>{r["Partner"]}</div>
                      <div style={S.sub}>{r["Email"]}</div>
                    </td>
                    <td style={S.td}>{r["Action"] ?? "-"}</td>
                    <td style={S.td}>{r["Deposit Type"] ?? "-"}</td>
                    <td style={S.td}>{r["Valuation Status"] ?? "-"}</td>
                    <td style={S.td}>{money(r["Cash/Account Transfer Amount"])}</td>
                    <td style={S.td}>{num(r["SSN Fund Value (Per Txn)"], 4)}</td>
                    <td style={S.td}>{num(r["SSN Units Bought (Per Txn)"])}</td>
                    <td style={S.td}>{num(r["SSN Total Units (Running Total)"])}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button className="atx-btn" style={S.rowBtn}
                        onClick={() => { setNotice(null); setMode({ kind: "edit", row: r }); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                        Edit
                      </button>
                      <button className="atx-del" style={{ ...S.rowBtn, ...S.deleteBtn }}
                        disabled={deleting === r.id} onClick={() => remove(r)}>
                        {deleting === r.id ? "Deleting…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows && visible.length === 0 && (
                  <tr><td style={{ ...S.td, color: "var(--text-muted)" }} colSpan={10}>No transactions match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  headerActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)" },
  count: { color: "var(--text-muted)", fontWeight: 400 },
  search: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 12px",
    color: "var(--text-primary)", fontSize: 13, minWidth: 220,
  },
  primaryBtn: {
    padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 },
  th: {
    textAlign: "left", padding: "10px 12px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)", whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px", color: "var(--text-primary)",
    borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap",
  },
  sub: { fontSize: 11, color: "var(--text-muted)" },
  rowBtn: {
    padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--text-primary)", marginLeft: 6,
  },
  deleteBtn: { borderColor: "rgba(224,90,90,0.4)", color: "#f08a8a" },
  success: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  error: {
    marginBottom: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13,
  },
};
